import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';
import { useAgendaStore } from '@/store/agendaStore';
import { Appointment } from '@/services/appointmentService';
import { useToast } from '@/components/ui/use-toast';
import { FileText, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ConvertToPracticeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment;
  onSuccess?: () => void;
}

export default function ConvertToPracticeDialog({
  open,
  onOpenChange,
  appointment,
  onSuccess,
}: ConvertToPracticeDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { practiceTypes, fetchPracticeTypes } = usePracticeTypeStore();
  const { convertToPractice, loading } = useAgendaStore();

  const [formData, setFormData] = useState({
    typeId: '',
    title: '',
    description: '',
    priority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT',
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPracticeTypes();
      // Pre-compila i campi
      setFormData({
        typeId: '',
        title: `Pratica da: ${appointment.title}`,
        description: appointment.notes || appointment.description || '',
        priority: 'MEDIUM',
      });
    }
  }, [open, appointment, fetchPracticeTypes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.typeId) {
      toast({
        title: 'Errore',
        description: 'Seleziona un tipo pratica',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const result = await convertToPractice(appointment.id, {
        typeId: formData.typeId,
        title: formData.title || undefined,
        description: formData.description || undefined,
        priority: formData.priority,
      });

      toast({
        title: 'Pratica creata',
        description: 'L\'appuntamento è stato convertito in pratica con successo.',
      });

      onOpenChange(false);
      onSuccess?.();

      // Opzionale: naviga alla pratica appena creata
      if (result?.practice?.id) {
        const shouldNavigate = confirm('Vuoi aprire la pratica appena creata?');
        if (shouldNavigate) {
          navigate(`/practices/${result.practice.id}`);
        }
      }
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.response?.data?.message || 'Impossibile convertire l\'appuntamento in pratica',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activePracticeTypes = practiceTypes.filter((t) => t.isActive);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Converti in Pratica
          </DialogTitle>
          <DialogDescription>
            Crea una nuova pratica a partire da questo appuntamento. L'appuntamento verrà automaticamente collegato alla pratica.
          </DialogDescription>
        </DialogHeader>

        {!appointment.clientId && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Attenzione:</strong> Questo appuntamento non ha un cliente associato. 
              Per convertirlo in pratica, devi prima assegnare un cliente.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo Pratica */}
          <div className="space-y-2">
            <Label htmlFor="typeId">Tipo Pratica *</Label>
            <Select
              value={formData.typeId}
              onValueChange={(value) => setFormData({ ...formData, typeId: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona tipo pratica..." />
              </SelectTrigger>
              <SelectContent>
                {activePracticeTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      {type.color && (
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: type.color }}
                        />
                      )}
                      <span>{type.name}</span>
                      <span className="text-gray-400 text-xs">({type.code})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Titolo */}
          <div className="space-y-2">
            <Label htmlFor="title">Titolo Pratica</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titolo della pratica..."
            />
          </div>

          {/* Descrizione */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione della pratica..."
              rows={3}
            />
          </div>

          {/* Priorità */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priorità</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData({ ...formData, priority: value as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Bassa</SelectItem>
                <SelectItem value="MEDIUM">Media</SelectItem>
                <SelectItem value="HIGH">Alta</SelectItem>
                <SelectItem value="URGENT">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Info appuntamento */}
          <div className="p-3 bg-gray-50 rounded-md text-sm text-gray-600">
            <div className="font-medium mb-1">Appuntamento:</div>
            <div>{appointment.title}</div>
            {appointment.client && (
              <div className="text-gray-500">
                Cliente: {appointment.client.firstName} {appointment.client.lastName}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !formData.typeId || !appointment.clientId}
            >
              {submitting ? 'Conversione...' : 'Crea Pratica'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
